import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';

export interface ActiveCall {
  id: string;
  contactUid: string;
  contactName: string;
  isVideo: boolean;
  isIncoming: boolean;
  startTime: number;
  onReturn: () => void;
  onEnd: () => void;
}

interface CallContextValue {
  activeCall: ActiveCall | null;
  isMinimized: boolean;
  registerCall: (call: Omit<ActiveCall, 'id' | 'startTime'>) => string;
  minimizeCall: () => void;
  maximizeCall: () => void;
  endCall: () => void;
  clearCall: () => void; // reset state without triggering onEnd
  updateCallDuration: () => number; // returns elapsed seconds
}

const CallContext = createContext<CallContextValue | null>(null);

export function CallProvider({ children }: { children: ReactNode }) {
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const callIdRef = useRef(0);

  const registerCall = useCallback((call: Omit<ActiveCall, 'id' | 'startTime'>) => {
    const id = `call-${++callIdRef.current}`;
    setActiveCall({ ...call, id, startTime: Date.now() });
    setIsMinimized(false);
    return id;
  }, []);

  const minimizeCall = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeCall = useCallback(() => {
    if (activeCall?.onReturn) activeCall.onReturn();
    setIsMinimized(false);
  }, [activeCall]);

  const endCall = useCallback(() => {
    if (activeCall?.onEnd) activeCall.onEnd();
    setActiveCall(null);
    setIsMinimized(false);
  }, [activeCall]);

  const clearCall = useCallback(() => {
    setActiveCall(null);
    setIsMinimized(false);
  }, []);

  const updateCallDuration = useCallback(() => {
    if (!activeCall) return 0;
    return Math.floor((Date.now() - activeCall.startTime) / 1000);
  }, [activeCall]);

  return (
    <CallContext.Provider value={{ activeCall, isMinimized, registerCall, minimizeCall, maximizeCall, endCall, clearCall, updateCallDuration }}>
      {children}
    </CallContext.Provider>
  );
}

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error('useCall must be used within CallProvider');
  return ctx;
}
